/**
 * PipelineAdvisor — "¿cuánto gano por hora si mino X ore?"
 *
 *  - Settings (mining rate + yield + refining tax) en localStorage, auto-save.
 *  - Ship presets (Venture, Procurer, Hulk, etc.) para setup rápido.
 *  - Summary tiles: top ore, average ISK/hr, distribución de best_action.
 *  - Ranked table: cada ore con los 3 pipelines (sell_ore / refine /
 *    compress) — el ganador highlighted. Expandible para mostrar volumes.
 *  - Filter por best_action.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import {
  type PipelineSettings,
  DEFAULT_PIPELINE_SETTINGS,
  SHIP_PRESETS,
  loadPipelineSettings,
  savePipelineSettings,
} from '../lib/pipelineSettings'

type BestAction = 'sell_ore' | 'refine' | 'compress' | 'insufficient_data'

type Pipeline = {
  value_per_m3: number | null
  value_per_unit: number | null
  isk_per_hour: number | null
}

type Item = {
  type_id: number
  name: string
  volume_per_unit: number | null
  group_id: number
  pipelines: { sell_ore: Pipeline; refine: Pipeline; compress: Pipeline }
  best_action: BestAction
  best_isk_per_hour: number | null
}

const BEST_ACTION_FILTERS: Array<{ key: BestAction | 'all'; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'sell_ore', label: 'Vender ore' },
  { key: 'refine', label: 'Refinar' },
  { key: 'compress', label: 'Comprimir' },
  { key: 'insufficient_data', label: 'Sin datos' },
]

function bestActionClasses(a: BestAction): string {
  switch (a) {
    case 'sell_ore':
      return 'bg-eve-accent/10 border-eve-accent text-eve-accent'
    case 'refine':
      return 'bg-eve-success/10 border-eve-success text-eve-success'
    case 'compress':
      return 'bg-eve-warn/10 border-eve-warn text-eve-warn'
    case 'insufficient_data':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
  }
}

function bestActionLabel(a: BestAction): string {
  switch (a) {
    case 'sell_ore':
      return 'Vender ore'
    case 'refine':
      return 'Refinar'
    case 'compress':
      return 'Comprimir'
    case 'insufficient_data':
      return 'Sin datos'
  }
}

export function PipelineAdvisor() {
  const [settings, setSettings] = useState<PipelineSettings>(() => loadPipelineSettings())
  const [filter, setFilter] = useState<BestAction | 'all'>('all')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  useEffect(() => {
    savePipelineSettings(settings)
  }, [settings])

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: [
      'utility-pipeline-advice',
      forceRefetchKey,
      settings.mining_rate_m3_per_min,
      settings.final_yield_pct,
      settings.refining_tax_pct,
    ],
    queryFn: () =>
      api.pipelineAdvice(
        {
          mining_rate_m3_per_min: settings.mining_rate_m3_per_min,
          final_yield_pct: settings.final_yield_pct,
          refining_tax_pct: settings.refining_tax_pct,
        },
        force,
      ),
  })

  const items: Item[] = data?.items ?? []
  const summary = data?.summary

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filter !== 'all' && it.best_action !== filter) return false
      return true
    })
  }, [items, filter])

  const update = (patch: Partial<PipelineSettings>) => {
    setSettings((s) => ({ ...s, ...patch }))
  }

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Pipeline ISK/hour</div>
          <div className="text-eve-muted text-xs">
            Ranking de los 16 ores estándar: cuál rinde más por hora de mining,
            según tu setup.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Settings — mining rate + yield + refining tax */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 p-3 bg-eve-panel/40 border border-eve-border rounded-md">
        <NumberField
          label="Mining rate (m³/min)"
          value={settings.mining_rate_m3_per_min}
          onChange={(v) => update({ mining_rate_m3_per_min: v })}
          hint="Procurer base ≈ 300, Hulk ≈ 900"
        />
        <NumberField
          label="Yield efectivo (%)"
          value={settings.final_yield_pct}
          onChange={(v) => update({ final_yield_pct: v })}
          hint="50 base NPC, 75+ con skills + rigs"
        />
        <NumberField
          label="Refining tax (%)"
          value={settings.refining_tax_pct}
          onChange={(v) => update({ refining_tax_pct: v })}
          hint="0 refinery propio, 5 NPC station"
        />
        <div className="col-span-1 sm:col-span-3 flex justify-end flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-wider text-eve-muted self-center">
            Preset:
          </span>
          {SHIP_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => update({ mining_rate_m3_per_min: p.m3_per_min })}
              className="text-[11px] uppercase tracking-wider text-eve-muted hover:text-eve-accent border border-eve-border hover:border-eve-accent/40 px-2 py-0.5 rounded transition-colors"
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setSettings(DEFAULT_PIPELINE_SETTINGS)}
            className="text-[11px] uppercase tracking-wider text-eve-muted hover:text-eve-accent border border-eve-border hover:border-eve-accent/40 px-2 py-0.5 rounded transition-colors"
          >
            ↻ Reset
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && summary.ores_with_data > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <SummaryTile
            label="Top ore"
            value={summary.top_ore_name ?? '—'}
            sub={
              summary.top_isk_per_hour !== null
                ? `${formatISKShort(summary.top_isk_per_hour)} ISK/hr · ${bestActionLabel(
                    items.find((i) => i.type_id === summary.top_ore_id)?.best_action ??
                      'insufficient_data',
                  )}`
                : '—'
            }
            accent
          />
          <SummaryTile
            label="Top ISK/hr"
            value={
              summary.top_isk_per_hour !== null
                ? `${formatISKShort(summary.top_isk_per_hour)} ISK`
                : '—'
            }
            sub={`${data?.settings_used.m3_per_hour.toLocaleString('en-US')} m³/hr`}
            positive
          />
          <SummaryTile
            label="Promedio top ores"
            value={
              summary.avg_isk_per_hour !== null
                ? `${formatISKShort(summary.avg_isk_per_hour)} ISK`
                : '—'
            }
            sub={`${summary.ores_with_data}/${summary.total_ores} con datos`}
          />
          <SummaryTile
            label="Mejor estrategia"
            value={
              summary.refine_wins >= summary.sell_wins &&
              summary.refine_wins >= summary.compress_wins
                ? 'Refinar'
                : summary.compress_wins >= summary.sell_wins
                  ? 'Comprimir'
                  : 'Vender ore'
            }
            sub={`${summary.sell_wins} vender · ${summary.refine_wins} refinar · ${summary.compress_wins} comprimir`}
          />
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as BestAction | 'all')}
          className="bg-eve-panel border border-eve-border rounded-md px-2 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none"
        >
          {BEST_ACTION_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="text-eve-muted text-xs ml-auto">
          {filtered.length} / {items.length}
        </span>
      </div>

      {/* Earn / hour table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-eve-muted text-xs uppercase tracking-wider border-b border-eve-border">
              <th className="py-2 pr-3 font-medium">Ore</th>
              <th className="py-2 pr-3 font-medium text-right">Vender ore</th>
              <th className="py-2 pr-3 font-medium text-right">Refinar</th>
              <th className="py-2 pr-3 font-medium text-right">Comprimir</th>
              <th className="py-2 font-medium text-center">Mejor</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5}>
                  <LoadingSkeleton variant="rows" rows={4} />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-eve-muted py-8 text-center italic">
                  Sin matches para el filtro actual.
                </td>
              </tr>
            ) : (
              filtered.map((it) => <PipelineRow key={it.type_id} item={it} />)
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-eve-muted mt-3">
        Todos los valores asumen vender en <span className="text-eve-text">Jita</span> (buy
        orders) como destino final. Para hubs regionales usá el multi-hub tool.
        Arkonor y Mercoxit no tienen variant comprimida — su pipeline "comprimir"
        queda en blanco.
      </p>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function NumberField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  hint?: string
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-eve-muted text-xs">{label}</span>
      <input
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value)
          onChange(Number.isFinite(n) && n >= 0 ? n : 0)
        }}
        className="bg-eve-bg border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none font-mono"
      />
      {hint && <span className="text-eve-muted text-[10px] italic">{hint}</span>}
    </label>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  accent,
  positive,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  positive?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : accent
      ? 'text-eve-accent'
      : 'text-eve-text'
  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3">
      <div className="stat-label">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 truncate ${valueClass}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function PipelineRow({ item }: { item: Item }) {
  return (
    <tr className="border-b border-eve-border/40 hover:bg-eve-panel/30 transition-colors">
      <td className="py-2 pr-3">
        <div className="font-medium">{item.name}</div>
        <div className="text-[10px] text-eve-muted font-mono">
          {item.volume_per_unit !== null ? `${item.volume_per_unit.toFixed(2)} m³/u` : 'vol?'}
        </div>
      </td>
      <PipelineCell pipeline={item.pipelines.sell_ore} best={item.best_action === 'sell_ore'} />
      <PipelineCell pipeline={item.pipelines.refine} best={item.best_action === 'refine'} />
      <PipelineCell pipeline={item.pipelines.compress} best={item.best_action === 'compress'} />
      <td className="py-2 text-center">
        <span
          className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${bestActionClasses(item.best_action)}`}
        >
          {bestActionLabel(item.best_action)}
        </span>
        {item.best_isk_per_hour !== null && (
          <div className="text-[10px] text-eve-text font-mono mt-1">
            {formatISKShort(item.best_isk_per_hour)}
          </div>
        )}
      </td>
    </tr>
  )
}

function PipelineCell({ pipeline, best }: { pipeline: Pipeline; best: boolean }) {
  const valueClass = best
    ? 'text-eve-success font-semibold'
    : pipeline.isk_per_hour !== null
      ? 'text-eve-text'
      : 'text-eve-muted'
  return (
    <td className="py-2 pr-3 text-right">
      <div className={`font-mono ${valueClass}`}>
        {pipeline.isk_per_hour !== null ? `${formatISKShort(pipeline.isk_per_hour)}` : '—'}
      </div>
      {pipeline.value_per_m3 !== null && (
        <div className="text-[10px] text-eve-muted font-mono">
          {pipeline.value_per_m3.toFixed(0)}/m³
        </div>
      )}
    </td>
  )
}
